<template>
  <div id="app">
    <router-view v-if="$route.path !== '/'" />
    
    <template v-if="$route.path === '/'">
      <header>
        <h1><span class="cat-icon">🐱</span> 哈基米德表情包收集</h1>
        <p>哈基米表情收集(v1.0) -  <span class="cat-paw">🐾</span></p>
        
        <!-- 添加格言展示区域 -->
        <div class="quote-container">
          <div class="quote-content">
            <span class="quote-mark quote-mark-open">❝</span>
            <p class="quote-text">{{ quotes[currentQuoteIndex].text }}</p>
            <span class="quote-mark quote-mark-close">❞</span>
          </div>
          <p class="quote-author">—— {{ quotes[currentQuoteIndex].author }}</p>
        </div>
        
        <!-- 友情链接区域 -->
        <div class="friend-links-header">
          <h3><span class="cat-icon small">🔗</span> 友情链接</h3>
          <div class="links-container">
            <a href="https://www.eunie.online" target="_blank" class="friend-link">
              <img src="./assets/eunie.png" alt="Eunie的博客" class="link-icon">
              <span class="link-text">Eunie的博客</span>
            </a>
            <a href="https://mio.edu.kg" target="_blank" class="friend-link">
              <img src="./assets/mio.png" alt="弥央大学" class="link-icon">
              <span class="link-text">弥央大学</span>
            </a>
          </div>
        </div>
        
        <!-- 登录状态显示区域 -->
        <div class="auth-status">
          <div v-if="isLoggedIn" class="user-info">
            <span class="welcome-text">欢迎管理员</span>
            <button class="logout-button" @click="handleLogout">退出登录</button>
          </div>
        </div>
        
        <div class="search-container">
          <div class="search-bar">
            <input 
              type="text" 
              placeholder="搜索哈基米表情包..." 
              v-model="searchQuery"
              @keyup.enter="searchStickers" 
            />
            <button class="search-button" @click="searchStickers">搜索</button>
          </div>
          <div class="sort-container">
            <select v-model="sortOption" @change="handleSortChange" class="sort-select">
              <option value="likes_desc">按点赞量 ↓</option>
              <option value="likes_asc">按点赞量 ↑</option>
              <option value="dislikes_desc">按点踩量 ↓</option>
              <option value="dislikes_asc">按点踩量 ↑</option>
              <option value="createdAt_desc">按时间 (最新)</option>
              <option value="createdAt_asc">按时间 (最早)</option>
            </select>
          </div>
          <button class="upload-button" @click="showUploadDialog">上传表情包</button>
          <button class="api-button" @click="showApiInfoDialog">
            <span class="api-icon">🔌</span> API
          </button>
        </div>
      </header>
      
      <div class="stickers-container">
        <h2><span class="cat-icon">😺</span> 哈基米图库</h2>
        <div v-if="loading" class="loading-container">
          <p>加载中...</p>
        </div>
        <div v-else-if="error" class="error-container">
          <p>{{ error }}</p>
        </div>
        <div v-else class="stickers-grid">
          <StickerCard 
            v-for="sticker in stickers" 
            :key="sticker.id" 
            :sticker="sticker"
            @sticker-deleted="handleStickerDeleted"
          />
        </div>
        <StickerPagination 
          :total-pages="totalPages" 
          :current-page="currentPage" 
          @page-change="handlePageChange"
        />
        
        <!-- 添加图片总数显示 -->
        <div class="sticker-stats">
          <p>
            <span class="stats-icon">📊</span> 
            本站目前共收录了 <span class="stats-highlight">{{ totalStickerCount }}</span> 张哈基米表情包
          </p>
        </div>
      </div>

      <!-- 上传对话框组件 -->
      <UploadDialog 
        v-model:visible="uploadDialogVisible"
        @upload-success="handleUploadSuccess"
      />
      
      <!-- API信息对话框组件 -->
      <ApiInfoDialog
        v-model:visible="apiInfoDialogVisible"
      />
      
      <!-- 通知系统 -->
      <NotificationSystem ref="notifications" />
      
      <!-- 网站声明区域 -->
      <footer class="site-footer">
        <div class="disclaimer">
          <h3><span class="cat-icon small">🐱</span> 网站声明</h3>
          <p>本站收录的哈基米表情包仅供娱乐与交流使用，所有内容均无意冒犯或引导任何形式的动物虐待行为。</p>
        </div>
        
        <!-- 版权声明区域 -->
        <div class="copyright">
          <p>&copy; 2025 哈基米德表情包收集 | Powered by Vue.js</p>
          <p>Made with <span class="heart">❤️</span> by AmamiyaHotaru</p>
          <p class="icp-link"><a href="https://icp.gov.moe/?keyword=20251551" target="_blank">萌ICP备20251551号</a></p>
        </div>
      </footer>
    </template>
  </div>
</template>

<script>
import StickerCard from './components/StickerCard.vue'
import StickerPagination from './components/Pagination.vue'
import UploadDialog from './components/UploadDialog.vue'
import NotificationSystem from './components/NotificationSystem.vue'
import ApiInfoDialog from './components/ApiInfoDialog.vue'
import { stickerApi } from './services/api'
import authService from './services/auth'

export default {
  name: 'App',
  components: {
    StickerCard,
    StickerPagination,
    UploadDialog,
    NotificationSystem,
    ApiInfoDialog
  },
  data() {
    return {
      stickers: [],
      currentPage: 1,
      pageSize: 12,
      totalItems: 0,
      totalStickerCount: null, // 网站总表情包数量(不受筛选影响)
      totalPages: 0,
      loading: true,
      error: null,
      sortBy: 'likes',
      order: 'desc',
      searchQuery: '',
      sortOption: 'likes_desc', // 默认按点赞量降序
      uploadDialogVisible: false,
      apiInfoDialogVisible: false,
      showSuccessNotification: false,
      isLoggedIn: false,
      // 格言数组，方便未来添加更多格言
      quotes: [
        {
          text: "给我一个书柜，我就能哈动整个地球",
          author: "哈基米德"
        }
      ],
      // 当前显示的格言索引
      currentQuoteIndex: 0
    }
  },
  created() {
    this.fetchStickers();
    // 检查登录状态
    this.checkLoginStatus();
  },
  methods: {
    async fetchStickers() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await stickerApi.getStickers(
          this.currentPage, 
          this.pageSize, 
          this.sortBy, 
          this.order,
          this.searchQuery
        );
        console.log('获取表情包原始数据:', response);
        
        // 处理新的API响应结构
        if (response.data && response.data.code === 200 && response.data.data) {
          const responseData = response.data.data;
          this.stickers = responseData.records
          this.totalItems = responseData.total;
          this.totalStickerCount = this.totalStickerCount!=null?this.totalStickerCount:responseData.total; // 更新总表情包数量
          this.totalPages = responseData.pages;
          this.currentPage = responseData.current;
          
          console.log('处理后的表情包数据:', this.stickers);
        } else {
          throw new Error('API返回格式错误');
        }
      } catch (err) {
        this.error = '获取表情包数据失败，请稍后重试';
        console.error('获取表情包数据错误:', err);
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchStickers();
    },
    searchStickers() {
      console.log(`执行搜索，关键词: "${this.searchQuery}"`);
      this.currentPage = 1;
      this.fetchStickers();
    },
    handleSortChange() {
      const [field, order] = this.sortOption.split('_');
      this.sortBy = field;
      this.order = order;
      console.log(`排序更改为: ${this.sortBy}, 顺序: ${this.order}`);
      
      this.currentPage = 1;
      this.fetchStickers();
    },
    showUploadDialog() {
      this.uploadDialogVisible = true;
    },
    showApiInfoDialog() {
      this.apiInfoDialogVisible = true;
    },
    handleUploadSuccess(data) {
      console.log('上传成功:', data);
      
      // 显示成功通知
      this.$refs.notifications.showSuccess('表情包上传成功!');
      
      // 本地更新总数计数(+1)
      this.totalStickerCount += 1;
      
      // 重新获取最新数据
      this.fetchStickers();
    },
    // 检查用户登录状态
    checkLoginStatus() {
      this.isLoggedIn = authService.isAuthenticated();
    },
    
    // 处理退出登录
    handleLogout() {
      authService.logout();
      this.isLoggedIn = false;
      this.$refs.notifications.showSuccess('已成功退出登录');
      
      // 强制重新获取数据，确保UI状态更新
      this.fetchStickers();
      
      // 通知所有子组件登录状态变化
      this.$nextTick(() => {
        // 发布登录状态变化的事件
        window.dispatchEvent(new CustomEvent('auth-state-changed', { 
          detail: { isLoggedIn: false }
        }));
      });
    },
    
    // 处理表情包删除
    handleStickerDeleted(stickerId) {
      // 从当前列表中移除被删除的表情包
      this.stickers = this.stickers.filter(sticker => sticker.id !== stickerId);
      
      // 本地更新总数计数(-1)
      this.totalStickerCount -= 1;
      
      // 重新获取数据以更新列表
      this.fetchStickers();
    },
    // 暴露通知方法供子组件使用
    showNotification(message, type = 'success') {
      this.$refs.notifications[`show${type.charAt(0).toUpperCase() + type.slice(1)}`](message);
    }
  },
  provide() {
    return {
      showNotification: this.showNotification
    };
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  background-color: transparent;
  background-image: url('./assets/bg.png');
  background-repeat: repeat;
  background-position: center;
}

#app {
  font-family: 'PingFang SC', 'Microsoft YaHei', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 15px;
  background-color: #f0f9f0;
  position: relative;
  z-index: 1;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
}

header {
  margin-bottom: 30px;
  padding: 15px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  width: 100%;
}

header h1 {
  color: #42b983;
  margin-bottom: 10px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cat-icon {
  margin-right: 8px;
  font-size: 1.2em;
}

.cat-paw {
  display: inline-block;
  animation: pawWave 2s infinite;
}

@keyframes pawWave {
  0% { transform: rotate(0deg); }
  25% { transform: rotate(20deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-20deg); }
  100% { transform: rotate(0deg); }
}

header p {
  color: #888;
  margin-bottom: 20px;
}

.search-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;
}

.search-bar {
  display: inline-flex;
  gap: 10px;
}

.search-bar input {
  padding: 8px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 250px;
}

.search-button {
  padding: 8px 15px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  height: 36px;
}

.search-button:hover {
  background-color: #3da576;
}

.sort-container {
  display: inline-flex;
  align-items: center;
}

.sort-select {
  padding: 8px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  min-width: 150px;
  color: #333;
  outline: none;
  transition: border-color 0.3s;
}

.sort-select:hover, .sort-select:focus {
  border-color: #42b983;
}

.upload-button {
  padding: 8px 15px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  height: 36px;
}

.upload-button:hover {
  background-color: #3da576;
}

.api-button {
  padding: 8px 15px;
  background-color: #7e57c2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 5px;
  height: 36px;
}

.api-button:hover {
  background-color: #6a4caf;
}

.api-icon {
  font-size: 16px;
}

h2 {
  text-align: left;
  margin: 20px 0;
  color: #42b983;
  display: flex;
  align-items: center;
  padding-left: 10px;
}

.stickers-container {
  padding: 15px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  width: 100%;
}

.stickers-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 30px;
  padding: 0 10px;
}

@media (max-width: 1200px) {
  .stickers-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 992px) {
  .stickers-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .stickers-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .stickers-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}

.loading-container, .error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
  color: #666;
}

/* 添加图片统计样式 */
.sticker-stats {
  text-align: center;
  margin-top: 10px;
  padding: 10px;
  color: #666;
  font-size: 14px;
  border-top: 1px dashed #e8f5e9;
}

.stats-icon {
  font-size: 16px;
  margin-right: 4px;
}

.stats-highlight {
  color: #42b983;
  font-weight: bold;
  font-size: 16px;
}

/* 成功提示样式 */
.success-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #42b983;
  color: white;
  padding: 15px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  display: flex;
  align-items: center;
  animation: slideIn 0.3s ease-out, fadeOut 0.5s ease-out 2.5s;
}

.success-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.success-icon {
  font-size: 20px;
  font-weight: bold;
}

@keyframes slideIn {
  from {
    transform: translateX(100px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

/* 登录状态样式 */
.auth-status {
  display: flex;
  justify-content: flex-end;
  margin: 10px 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.welcome-text {
  color: #42b983;
  font-weight: 500;
}

.logout-button {
  padding: 8px 15px;
  background-color: #ff7675;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  height: 36px;
  display: flex;
  align-items: center;
}

.logout-button:hover {
  background-color: #ff5252;
}

.login-button {
  padding: 6px 12px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  text-decoration: none;
  transition: background-color 0.3s;
}

.login-button:hover {
  background-color: #3da576;
}

/* 格言样式 */
.quote-container {
  margin: 10px auto;  /* 减小上下边距 */
  max-width: 100%;    /* 宽度改为100% */
  padding: 13px;      /* 减小内边距 */
  padding-bottom: 0px;
  background-color: #f5f5f5;
  border-radius: 10px;
  position: relative;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s, box-shadow 0.3s;
  border-left: 4px solid #42b983;
}

.quote-container:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.quote-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
}

.quote-text {
  font-size: 18px;    /* 减小字体大小 */
  font-weight: 500;
  color: #333;
  line-height: 1.5;   /* 减小行高 */
  letter-spacing: 1px;
  font-family: "楷体", KaiTi, "STKaiti", serif;
}

.quote-mark {
  font-size: 36px;
  color: #42b983;
  opacity: 0.5;
  font-family: serif;
  line-height: 1;
}

.quote-mark-open {
  align-self: flex-start;
  margin-right: 10px;
  margin-top: -10px;
}

.quote-mark-close {
  align-self: flex-end;
  margin-left: 10px;
  margin-bottom: -10px;
}

.quote-author {
  text-align: right;
  margin-top: 5px;   /* 减小上边距 */
  font-size: 16px;
  color: #666;
  font-weight: 500;
  font-style: italic;
  padding-right: 20px;
  font-family: "楷体", KaiTi, "STKaiti", serif;
}

@media (max-width: 768px) {
  .quote-container {
    max-width: 95%;
    padding: 15px;
  }
  
  .quote-text {
    font-size: 18px;
  }
  
  .quote-mark {
    font-size: 28px;
  }
}

/* 页脚样式 */
.site-footer {
  margin-top: 30px;
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  text-align: center;
}

.disclaimer {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #e8f5e9;
}

.disclaimer h3 {
  color: #42b983;
  margin-bottom: 10px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.disclaimer p {
  color: #555;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* 友情链接样式 */
.friend-links-header {
  margin: 10px 0 20px;
  padding: 12px;
  background-color: #f8fbf8;
  border-radius: 8px;
  border: 1px dashed #c8e6c9;
}

.friend-links-header h3 {
  color: #42b983;
  margin-bottom: 15px;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.friend-links-header .links-container {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.friend-links-header .friend-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #42b983;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: #fff;
  border: 1px solid #e8f5e9;
  transition: all 0.3s ease;
  font-size: 14px;
}

.friend-links-header .friend-link:hover {
  background-color: #e8f5e9;
  border-color: #42b983;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(66, 185, 131, 0.2);
}

.friend-links-header .link-icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  margin-right: 8px;
  transition: transform 0.3s ease;
}

.friend-links-header .friend-link:hover .link-icon {
  transform: scale(1.1);
}

.friend-links-header .link-text {
  font-weight: 500;
}

/* 响应式设计 - 页头友情链接 */
@media (max-width: 768px) {
  .friend-links-header .links-container {
    gap: 10px;
  }
  
  .friend-links-header .friend-link {
    padding: 6px 10px;
    font-size: 13px;
  }
  
  .friend-links-header .link-icon {
    width: 20px;
    height: 20px;
  }
}

/* 登录状态样式 */
.auth-status {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 15px;
  background-color: #f8fbf8;
  border-radius: 6px;
  border: 1px solid #c8e6c9;
}

.welcome-text {
  color: #42b983;
  font-size: 14px;
  font-weight: 500;
}

.logout-button {
  padding: 4px 12px;
  background-color: #ff5252;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.3s;
}

.logout-button:hover {
  background-color: #d32f2f;
}

/* 页脚样式 */
.site-footer {
  margin-top: 30px;
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  text-align: center;
}

.disclaimer {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #e8f5e9;
}

.disclaimer h3 {
  color: #42b983;
  margin-bottom: 10px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.disclaimer p {
  color: #555;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* 版权声明样式 */
.copyright {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #e8f5e9;
}

.copyright p {
  color: #888;
  font-size: 12px;
  margin: 5px 0;
  line-height: 1.4;
}

.heart {
  color: #ff5252;
  animation: heartbeat 1.5s ease-in-out infinite;
}

@keyframes heartbeat {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.cat-icon.small {
  font-size: 0.9em;
  margin: 0 2px;
}
</style>
